/**
 * EXCEPCIONES PUNTUALES DE DISPONIBILIDAD — FASE 6.3 §I / FINAL GATE §1
 *
 * `NO_DISPONIBLE` (día completo) y `DISPONIBILIDAD_EXTRA` (con horas)
 * sobre `excepciones_disponibilidad_operario` (FASE 6.1 §F). El
 * contrato actual no tiene `PATCH` — sólo `POST`/`DELETE` — y esta
 * pantalla NO lo simula con un DELETE seguido de un POST: si el DELETE
 * tuviera éxito y el POST fallara después, la excepción original se
 * perdería sin que nadie lo pidiera. Sólo hay crear y eliminar; para
 * "cambiar" una excepción, el usuario la elimina y crea otra de forma
 * explícita — dos acciones separadas, cada una atómica por sí misma.
 *
 * Nunca se pide ni se guarda un motivo: el texto es siempre neutro
 * («No disponible» / «Disponibilidad adicional»), igual que ya impone el
 * modelo (0048: la tabla no tiene columna de motivo).
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { api, type ExcepcionDisponibilidad, type TipoExcepcionDisponibilidad } from '../api.ts';
import { Error as ErrorCaja, Panel, fecha, useDatos, Vacio } from './base.tsx';

export function ExcepcionesDisponibilidad({ idOperario, puedeEditar }: { idOperario: string; puedeEditar: boolean }): ReactNode {
  const excepciones = useDatos(
    () => api.get<{ excepciones: ExcepcionDisponibilidad[] }>(`/api/operarios/${idOperario}/disponibilidad/excepciones`),
    [idOperario],
  );
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h2>Excepciones puntuales</h2>
        {puedeEditar && (
          <button type="button" className="pequeno" onClick={() => setMostrarFormulario((v) => !v)}>
            {mostrarFormulario ? 'Cancelar' : 'Nueva excepción'}
          </button>
        )}
      </div>

      {mostrarFormulario && (
        <FormExcepcion
          idOperario={idOperario}
          alGuardar={() => {
            setMostrarFormulario(false);
            excepciones.recargar();
          }}
          alCancelar={() => setMostrarFormulario(false)}
        />
      )}

      <div className="panel-cuerpo sin-relleno">
        <Panel estado={excepciones}>
          {(e) =>
            e.excepciones.length === 0 ? (
              <Vacio titulo="Sin excepciones puntuales" />
            ) : (
              <div className="tabla-desliza">
                <table>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Horario</th>
                      {puedeEditar && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {e.excepciones.map((ex) => (
                      <FilaExcepcion key={ex.id} idOperario={idOperario} excepcion={ex} puedeEditar={puedeEditar} onBorrada={excepciones.recargar} />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Panel>
      </div>
    </div>
  );
}

function etiquetaTipo(tipo: string): string {
  return tipo === 'DISPONIBILIDAD_EXTRA' ? 'Disponibilidad adicional' : 'No disponible';
}

function FilaExcepcion({
  idOperario,
  excepcion,
  puedeEditar,
  onBorrada,
}: {
  idOperario: string;
  excepcion: ExcepcionDisponibilidad;
  puedeEditar: boolean;
  onBorrada: () => void;
}): ReactNode {
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function borrar(): Promise<void> {
    setBorrando(true);
    setError(null);
    try {
      await api.del(`/api/operarios/${idOperario}/disponibilidad/excepciones/${excepcion.id}`);
      onBorrada();
    } catch (err) {
      setError(err);
      setBorrando(false);
    }
  }

  return (
    <tr>
      <td className="mono">{fecha(excepcion.fecha)}</td>
      <td>
        <span className={`etiqueta ${excepcion.tipo === 'DISPONIBILIDAD_EXTRA' ? 'exito' : 'neutra'}`}>{etiquetaTipo(excepcion.tipo)}</span>
      </td>
      <td>{excepcion.horaInicio !== null && excepcion.horaFin !== null ? `${excepcion.horaInicio}–${excepcion.horaFin}` : 'Todo el día'}</td>
      {puedeEditar && (
        <td className="derecha">
          {error !== null ? (
            <span className="pequena tenue">Error al eliminar.</span>
          ) : confirmando ? (
            <span className="fila">
              <button type="button" className="pequeno" onClick={() => { void borrar(); }} disabled={borrando}>
                {borrando ? '…' : 'Confirmar'}
              </button>
              <button type="button" className="pequeno" onClick={() => setConfirmando(false)} disabled={borrando}>
                Cancelar
              </button>
            </span>
          ) : (
            <button type="button" className="pequeno" onClick={() => setConfirmando(true)}>
              Eliminar
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

function FormExcepcion({
  idOperario,
  alGuardar,
  alCancelar,
}: {
  idOperario: string;
  alGuardar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const hoy = new Date().toISOString().slice(0, 10);
  const [fechaVal, setFechaVal] = useState(hoy);
  const [tipo, setTipo] = useState<TipoExcepcionDisponibilidad>('NO_DISPONIBLE');
  const [horaInicio, setHoraInicio] = useState('16:00');
  const [horaFin, setHoraFin] = useState('20:00');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/operarios/${idOperario}/disponibilidad/excepciones`, {
        fecha: fechaVal,
        tipo,
        horaInicio: tipo === 'DISPONIBILIDAD_EXTRA' ? horaInicio : undefined,
        horaFin: tipo === 'DISPONIBILIDAD_EXTRA' ? horaFin : undefined,
      });
      alGuardar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <div className="campo">
          <label htmlFor="excepcion-fecha">Fecha</label>
          <input id="excepcion-fecha" type="date" value={fechaVal} onChange={(e) => setFechaVal(e.target.value)} disabled={enviando} required />
        </div>
        <div className="campo">
          <label htmlFor="excepcion-tipo">Tipo</label>
          <select id="excepcion-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoExcepcionDisponibilidad)} disabled={enviando}>
            <option value="NO_DISPONIBLE">No disponible (todo el día)</option>
            <option value="DISPONIBILIDAD_EXTRA">Disponibilidad adicional (con horario)</option>
          </select>
        </div>
      </div>
      {tipo === 'DISPONIBILIDAD_EXTRA' && (
        <div className="form-grupo">
          <div className="campo">
            <label htmlFor="excepcion-hora-inicio">Hora inicio</label>
            <input id="excepcion-hora-inicio" type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} disabled={enviando} />
          </div>
          <div className="campo">
            <label htmlFor="excepcion-hora-fin">Hora fin</label>
            <input id="excepcion-hora-fin" type="time" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} disabled={enviando} />
          </div>
        </div>
      )}
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Añadir excepción'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
